import { Text, Card, CardHeader, Badge } from '@fluentui/react-components';
import { CloudRegular, FolderRegular, DocumentRegular, CheckmarkCircleRegular, EditRegular } from '@fluentui/react-icons';
import type { Project } from '../services/project-service';

interface DashboardPageProps {
  projects?: Project[];
  documentCounts?: { total: number; draft: number; review: number; final: number };
  onSelectProject?: (project: Project) => void;
}

export function DashboardPage({ projects = [], documentCounts, onSelectProject }: DashboardPageProps) {
  const activeProjects = projects.filter(p => p.status === 'active').length;
  const completedProjects = projects.filter(p => p.status === 'complete').length;
  const totalDocs = documentCounts?.total ?? 0;
  const finalDocs = documentCounts?.final ?? 0;
  const completionRate = totalDocs > 0 ? Math.round(100 * finalDocs / totalDocs) : 0;

  return (
    <div className="page-root page-root--scrollable dashboard-page">
      <div className="page-title-bar">
        <CloudRegular className="icon-title" />
        <Text size={500} weight="semibold">Dashboard</Text>
      </div>

      <div className="dashboard-grid">
        <Card className="dashboard-card">
          <CardHeader
            image={<FolderRegular className="dashboard-card-icon" />}
            header={<Text weight="semibold">Projects</Text>}
            description={<Text size={600} weight="bold">{projects.length}</Text>}
          />
          <div className="dashboard-card-detail">
            <Badge size="small" color="success">{activeProjects} active</Badge>
            <Badge size="small" appearance="outline">{completedProjects} complete</Badge>
          </div>
        </Card>

        <Card className="dashboard-card">
          <CardHeader
            image={<DocumentRegular className="dashboard-card-icon" />}
            header={<Text weight="semibold">Documents</Text>}
            description={<Text size={600} weight="bold">{totalDocs}</Text>}
          />
          <div className="dashboard-card-detail">
            <Badge size="small" color="warning">{documentCounts?.draft ?? 0} draft</Badge>
            <Badge size="small" color="brand">{documentCounts?.review ?? 0} review</Badge>
            <Badge size="small" color="success">{documentCounts?.final ?? 0} final</Badge>
          </div>
        </Card>

        <Card className="dashboard-card">
          <CardHeader
            image={<CheckmarkCircleRegular className="dashboard-card-icon" />}
            header={<Text weight="semibold">Completion Rate</Text>}
            description={<Text size={600} weight="bold">{completionRate}%</Text>}
          />
          <div className="dashboard-card-detail">
            <Text size={200} className="text-muted">{finalDocs} of {totalDocs} documents finalised</Text>
          </div>
        </Card>

        <Card className="dashboard-card">
          <CardHeader
            image={<EditRegular className="dashboard-card-icon" />}
            header={<Text weight="semibold">CAF Phases</Text>}
            description={<Text size={600} weight="bold">Strategy → Adopt</Text>}
          />
          <div className="dashboard-card-detail">
            <Text size={200} className="text-muted">15 templates across 5 phases</Text>
          </div>
        </Card>
      </div>

      <div className="dashboard-section">
        <Text size={400} weight="semibold">Recent Projects</Text>
        <table className="data-table">
          <thead>
            <tr>
              <th>Project</th>
              <th>Customer</th>
              <th>Phase</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {projects.slice(0, 5).map(p => (
              <tr key={p.id} className="clickable" onClick={() => onSelectProject?.(p)}>
                <td><Text weight="semibold">{p.name}</Text></td>
                <td className="text-muted">{p.customerId}</td>
                <td><Badge size="small" appearance="outline">{p.cafPhase}</Badge></td>
                <td><Badge size="small" color={p.status === 'active' ? 'success' : 'informative'}>{p.status}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
